
import { defineComponent } from 'vue'
import BaseEditDialog from '@madxnl/mrrabbit/dist/EditDialog'
import { submitEditDialog } from './edit-dialog-submit'
import { EXERCISE_DATA_STYLE_DEFAULT } from './exercise-data-style'

type ConditionValue = string | number | boolean | null
type FieldCondition = {
  field: string
  equals?: ConditionValue
  in?: ConditionValue[]
  notIn?: ConditionValue[]
}

type ExtendedField = {
  type?: string
  readOnly?: boolean
  label?: string
  minExclusive?: number
  endOfDay?: boolean
  helpText?: string
  placeholder?: string
  labelsByAlgorithm?: Record<string, string>
  showWhen?: FieldCondition | FieldCondition[]
  hideWhen?: FieldCondition | FieldCondition[]
}

const baseFormatLabel = (BaseEditDialog as any)?.methods?.formatLabel
const baseGetInputType = (BaseEditDialog as any)?.methods?.getInputType
const baseCleanModel = (BaseEditDialog as any)?.methods?.cleanModel
const baseValidate = (BaseEditDialog as any)?.methods?.validate

export default defineComponent({
  name: 'EditDialog',
  extends: BaseEditDialog as any,
  methods: {
    submit() {
      // The dialog can replace addObj after the input has mounted. Persist the
      // displayed default before required-field validation and serialization.
      for (const [fieldName, field] of Object.entries(this.editableFields())) {
        if (field.type === 'exerciseDataStyle' && (this as any).addObj[fieldName] == null) {
          (this as any).addObj[fieldName] = EXERCISE_DATA_STYLE_DEFAULT
        }
      }
      const missingFields = typeof baseValidate === 'function' ? baseValidate.call(this) : []
      return submitEditDialog(this as any, missingFields)
    },

    getInputType(type: string) {
      if (type === 'datePicker') return 'DatePickerInput'
      if (type === 'exerciseDataStyle') return 'ExerciseDataStyleInput'
      return typeof baseGetInputType === 'function' ? baseGetInputType.call(this, type) : 'InputText'
    },

    cleanModel(model: Record<string, unknown>, fieldConfig: Record<string, ExtendedField>) {
      const cleaned = typeof baseCleanModel === 'function'
        ? baseCleanModel.call(this, model, fieldConfig)
        : { ...model }

      // Mrrabbit normally removes nulls. A date-picker null is intentional:
      // it is how an existing optional expiry date is cleared in GraphQL.
      for (const [fieldName, field] of Object.entries(fieldConfig ?? {})) {
        if (field.type === 'datePicker' && model[fieldName] === null) {
          cleaned[fieldName] = null
        }
      }

      return cleaned
    },

    editableFields(config?: { editPage?: { fields?: Record<string, ExtendedField> } }) {
      const editFields: Record<string, ExtendedField> = {}
      const resolvedConfig = config ?? ((this as any).config as { editPage?: { fields?: Record<string, ExtendedField> } })
      const fields = resolvedConfig?.editPage?.fields ?? {}

      for (const fieldName of Object.keys(fields)) {
        const field = fields[fieldName] as ExtendedField
        if (field?.type == null || field.readOnly === true) {
          continue
        }
        if (!this.isFieldVisible(field)) {
          continue
        }
        editFields[fieldName] = field
      }

      return editFields
    },

    formatLabel(fieldName: string) {
      const fieldConfig: ExtendedField | undefined = (this as any).config?.editPage?.fields?.[fieldName]
      if (fieldConfig == null) {
        return typeof baseFormatLabel === 'function' ? baseFormatLabel.call(this, fieldName) : fieldName
      }

      const activeAlgorithm = (this as any).addObj?.hitDetectionAlgorithm ?? (this as any).value?.hitDetectionAlgorithm
      if (
        activeAlgorithm != null &&
        fieldConfig.labelsByAlgorithm != null &&
        typeof fieldConfig.labelsByAlgorithm[activeAlgorithm] === 'string'
      ) {
        return fieldConfig.labelsByAlgorithm[activeAlgorithm]
      }

      if (typeof fieldConfig.label === 'string' && fieldConfig.label.length > 0) {
        return fieldConfig.label
      }

      return typeof baseFormatLabel === 'function' ? baseFormatLabel.call(this, fieldName) : fieldName
    },

    isFieldVisible(field: ExtendedField): boolean {
      const shouldShow = this.matchConditions(field.showWhen, true)
      if (!shouldShow) {
        return false
      }
      const shouldHide = this.matchConditions(field.hideWhen, false)
      return !shouldHide
    },

    matchConditions(
      conditions: FieldCondition | FieldCondition[] | undefined,
      defaultWhenEmpty: boolean,
    ): boolean {
      if (conditions == null) {
        return defaultWhenEmpty
      }

      const list = Array.isArray(conditions) ? conditions : [conditions]
      for (const condition of list) {
        const currentValue = (this as any).addObj?.[condition.field] ?? (this as any).value?.[condition.field]

        if (condition.equals !== undefined && currentValue !== condition.equals) {
          return false
        }

        if (Array.isArray(condition.in) && !condition.in.includes(currentValue)) {
          return false
        }

        if (Array.isArray(condition.notIn) && condition.notIn.includes(currentValue)) {
          return false
        }
      }

      return true
    },
  },
})
